/**
 * PortalAffiliates — Affiliate management + revenue share portal
 *
 * Shows all affiliates, their earnings, payouts, and status.
 * Affiliates can also view their own dashboard after login.
 */

import React, { useState, useEffect, useCallback } from 'react';
import PortalLayout from '@/components/portal/PortalLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Users, DollarSign, TrendingUp, Plus, Loader2, Copy, Check,
  ExternalLink, UserCheck, Clock, Pause,
} from 'lucide-react';
import { supabaseGet, supabasePost, supabasePatch } from '@/utils/supabaseRest';
import { useAuth } from '@/contexts/AuthContext';

interface Affiliate {
  id: string;
  affiliate_code: string;
  company_name: string | null;
  contact_name: string | null;
  contact_email: string;
  status: string;
  rev_share_percent: number;
  tier: string;
  created_at: string;
}

interface Earning {
  id: string;
  affiliate_id: string;
  gross_revenue: number;
  affiliate_share: number;
  platform_share: number;
  status: string;
  vertical_id: string | null;
  created_at: string;
}

export default function PortalAffiliates() {
  const { user } = useAuth();
  const [affiliates, setAffiliates] = useState<Affiliate[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);

  // Create form state
  const [formEmail, setFormEmail] = useState('');
  const [formName, setFormName] = useState('');
  const [formCompany, setFormCompany] = useState('');
  const [formRevShare, setFormRevShare] = useState(30);
  const [creating, setCreating] = useState(false);

  const loadAffiliates = useCallback(async () => {
    setLoading(true);
    try {
      const data = await supabaseGet('affiliates?order=created_at.desc');
      setAffiliates(data || []);
    } catch (err) {
      console.error('Failed to load affiliates:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAffiliates(); }, [loadAffiliates]);

  const generateCode = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let code = 'AFF-';
    for (let i = 0; i < 4; i++) code += chars.charAt(Math.floor(Math.random() * chars.length));
    return code;
  };

  const createAffiliate = async () => {
    if (!formEmail) return;
    if (!user?.id) { console.error('No user ID'); return; }
    setCreating(true);
    try {
      await supabasePost('affiliates', {
        user_id: user?.id,
        affiliate_code: generateCode(),
        contact_email: formEmail,
        contact_name: formName || null,
        company_name: formCompany || null,
        rev_share_percent: formRevShare,
        status: 'active',
      });
      setShowCreate(false);
      setFormEmail('');
      setFormName('');
      setFormCompany('');
      loadAffiliates();
    } catch (err) {
      console.error('Failed to create affiliate:', err);
    } finally {
      setCreating(false);
    }
  };

  const toggleStatus = async (aff: Affiliate) => {
    const newStatus = aff.status === 'active' ? 'paused' : 'active';
    try {
      await supabasePatch(`affiliates?id=eq.${aff.id}`, { status: newStatus });
      setAffiliates(prev => prev.map(a => a.id === aff.id ? { ...a, status: newStatus } : a));
    } catch (err) {
      console.error('Failed to toggle affiliate:', err);
    }
  };

  const copyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    setCopied(code);
    setTimeout(() => setCopied(null), 2000);
  };

  const totalAffiliates = affiliates.length;
  const activeAffiliates = affiliates.filter(a => a.status === 'active').length;

  const embedSnippet = (code: string) =>
    `<script src="https://intentdrive.ai/embed.js"\n  data-affiliate-id="${code}"\n  data-vertical="auto_insurance"\n  data-source="${code}"></script>`;

  return (
    <PortalLayout>
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Users className="h-6 w-6 text-emerald-400" />
              Affiliates
            </h1>
            <p className="text-sm text-slate-400 mt-1">
              Manage revenue share partners. Each affiliate gets a unique tracking code and embed snippet.
            </p>
          </div>
          <Button onClick={() => setShowCreate(!showCreate)} className="bg-emerald-600 hover:bg-emerald-700">
            <Plus className="h-4 w-4 mr-1" /> Add Affiliate
          </Button>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-3 gap-3">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-white">{totalAffiliates}</div>
              <div className="text-xs text-slate-400">Total Affiliates</div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-emerald-400">{activeAffiliates}</div>
              <div className="text-xs text-slate-400">Active</div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-violet-400">{totalAffiliates - activeAffiliates}</div>
              <div className="text-xs text-slate-400">Paused</div>
            </CardContent>
          </Card>
        </div>

        {/* Create form */}
        {showCreate && (
          <Card className="bg-slate-800/50 border-emerald-500/30">
            <CardContent className="p-5 space-y-4">
              <h3 className="text-sm font-medium text-white">New Affiliate</h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Email *</label>
                  <input value={formEmail} onChange={e => setFormEmail(e.target.value)} placeholder="affiliate@example.com" className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm" />
                </div>
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Contact Name</label>
                  <input value={formName} onChange={e => setFormName(e.target.value)} placeholder="John Smith" className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm" />
                </div>
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Company</label>
                  <input value={formCompany} onChange={e => setFormCompany(e.target.value)} placeholder="Acme Marketing" className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm" />
                </div>
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Rev Share %</label>
                  <input type="number" min={1} max={90} value={formRevShare} onChange={e => setFormRevShare(parseInt(e.target.value) || 30)} className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm" />
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={createAffiliate} disabled={creating || !formEmail} className="bg-emerald-600 hover:bg-emerald-700">
                  {creating ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                  Create Affiliate
                </Button>
                <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Affiliates list */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 text-emerald-400 animate-spin" />
          </div>
        ) : affiliates.length === 0 ? (
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-8 text-center">
              <Users className="h-10 w-10 text-slate-600 mx-auto mb-3" />
              <p className="text-sm text-slate-400">No affiliates yet. Add your first revenue share partner.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {affiliates.map((aff) => (
              <Card key={aff.id} className={`border transition-colors ${aff.status === 'active' ? 'bg-slate-800/50 border-slate-700' : 'bg-slate-900/50 border-slate-800 opacity-60'}`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center shrink-0">
                      {aff.status === 'active' ? <UserCheck className="h-5 w-5 text-emerald-400" /> : <Pause className="h-5 w-5 text-slate-500" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-white">{aff.contact_name || aff.contact_email}</span>
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${aff.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-600/20 text-slate-400'}`}>{aff.status}</span>
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-violet-500/20 text-violet-400">{aff.rev_share_percent}% rev share</span>
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">{aff.company_name || aff.contact_email}</div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        onClick={() => copyCode(aff.affiliate_code)}
                        className="flex items-center gap-1 px-2 py-1 rounded bg-slate-700 text-xs text-slate-300 hover:bg-slate-600"
                      >
                        {copied === aff.affiliate_code ? <Check className="h-3 w-3 text-emerald-400" /> : <Copy className="h-3 w-3" />}
                        {aff.affiliate_code}
                      </button>
                      <Button size="sm" variant="outline" onClick={() => toggleStatus(aff)}>
                        {aff.status === 'active' ? 'Pause' : 'Activate'}
                      </Button>
                    </div>
                  </div>
                  {/* Embed code preview */}
                  <details className="mt-3">
                    <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-300">View embed code</summary>
                    <pre className="mt-2 p-3 bg-slate-900 rounded-lg text-xs text-emerald-400 overflow-x-auto">{embedSnippet(aff.affiliate_code)}</pre>
                  </details>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </PortalLayout>
  );
}
